import { useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GithubIcon, JiraIcon, PageHeader, Spinner } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { useConnections } from '../../lib/queries';
import { toast } from '../../lib/store';
import { connectHref } from './IntegrationsPage';

// Settings › Connections: your own GitHub and Jira sign-ins. Connect once; every project you create
// (and anything you approve) uses them. Tokens are encrypted and refresh automatically.

export default function ConnectionsPage() {
  const qc = useQueryClient();
  const conns = useConnections();
  const [params, setParams] = useSearchParams();

  // Back from the provider: say how it went, then tidy the URL.
  useEffect(() => {
    const ok = params.get('connected');
    const err = params.get('connect_error');
    if (!ok && !err) return;
    if (ok) toast(`${ok === 'github' ? 'GitHub' : 'Jira'} connected`);
    if (err) toast(err, 'error');
    void qc.invalidateQueries();
    setParams({}, { replace: true });
  }, [params, setParams, qc]);

  const disconnect = async (p: 'github' | 'atlassian') => {
    try {
      await api.del(`/api/connections/${p}`);
      await qc.invalidateQueries();
      toast(`${p === 'github' ? 'GitHub' : 'Jira'} disconnected`);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  if (conns.isLoading) return <Spinner />;
  const c = conns.data;
  if (!c) return <div className="banner">{errorText(conns.error)}</div>;

  return (
    <div className="settings-content narrow">
      <PageHeader title="Connections" sub="Sign in with GitHub and Jira once. Your projects use these connections, so pushes, pull requests and ticket updates act as you." />

      <section className="card card-pad stack-sm">
        <div className="section-title row" style={{ gap: 8 }}>
          <GithubIcon /> GitHub
        </div>
        {c.github.connected ? (
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span>
              Connected as <b>@{c.github.account}</b>
            </span>
            <button className="btn btn-ghost" onClick={() => void disconnect('github')}>
              Disconnect
            </button>
          </div>
        ) : c.github.available ? (
          <div className="row" style={{ gap: 10 }}>
            <a className="btn btn-primary" href={connectHref('github')}>
              <GithubIcon />
              Connect GitHub
            </a>
            <span className="hint">You'll approve the Recite Me GitHub App, then come straight back.</span>
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 14 }}>
            Not set up yet: an admin adds the GitHub App in Admin › Integrations.
          </div>
        )}
        {c.github.installUrl && (
          <div className="hint">
            Missing a repo? The App only sees repos where it's installed.{' '}
            <a href={c.github.installUrl} target="_blank" rel="noopener noreferrer">
              Install or configure it <ExternalLink className="icon" style={{ fontSize: 12 }} />
            </a>
          </div>
        )}
      </section>

      <section className="card card-pad stack-sm">
        <div className="section-title row" style={{ gap: 8 }}>
          <JiraIcon /> Jira
        </div>
        {c.atlassian.connected ? (
          <>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span>
                Connected as <b>{c.atlassian.account}</b>
              </span>
              <button className="btn btn-ghost" onClick={() => void disconnect('atlassian')}>
                Disconnect
              </button>
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
              Sites: {c.atlassian.sites.map((s) => s.name).join(', ') || 'none'}
            </div>
          </>
        ) : c.atlassian.available ? (
          <div className="row" style={{ gap: 10 }}>
            <a className="btn btn-primary" href={connectHref('atlassian')}>
              <JiraIcon />
              Connect Jira
            </a>
            <span className="hint">Sign in with your Atlassian account and approve access to Jira.</span>
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 14 }}>
            Not set up yet: an admin adds the Atlassian app in Admin › Integrations.
          </div>
        )}
      </section>
    </div>
  );
}
